import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedRichText, LocalizedText } from "@/i18n/LocalizedText";
import React, { useEffect, useRef, useState } from "react";
import { auth, googleProvider, remoteConfig } from "../firebase";
import { signInWithPopup, signInWithRedirect } from "firebase/auth";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import noboriLogo from "../assets/logo.svg";
import { useForm, FormProvider } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Step1 from "./RegistrationStep1";
import Step2 from "./RegistrationStep2";
import Step3 from "./RegistrationStep3";
import Step4 from "./RegistrationStep4";
import { step1Schema, step2Schema, step3Schema, step4Schema } from "./schemas";
import { signOut } from "firebase/auth";
import { useNavigate, useParams } from "react-router-dom";
import { Check, LogOut, TriangleAlert } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { authAPI, invitesAPI, promptsAPI } from "../services/api";
import { useAuth } from "./AuthContext";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import type { AxiosError } from "axios";
import type { Brand } from "./AppSidebar";
import { useDataStore, type ClusterWithPrompt } from "@/store/useDataStore";
import { requireEmailVerification } from "@/config/auth";
import {
  capturePosthogEvent,
  clearOnboardingStartedAt,
  getOnboardingStartedAt,
  setBrandOnboardingCompletedAt,
  setOnboardingStartedAt,
  setPosthogPersonProperties,
} from "@/lib/posthog";
import {
  detectInAppBrowser,
  getGoogleAuthErrorMessage,
  type InAppBrowserSource,
  resolveGoogleSignInMode,
} from "@/lib/googleAuth";
import { AUTH_FLOW_DEFAULTS, parseAuthFlowConfig } from "@/lib/authFlowConfig";
import InAppBrowserGoogleAuthDialog from "@/components/InAppBrowserGoogleAuthDialog";
import registrationBg from "../assets/registrationBg.svg";
import { ClusterPromptsProcessing } from "./ClusterPromptProcessing";
import { stepDescription } from "@/services/helpers";

const requiredPhoneSchema = step1Schema.safeExtend({
  phone: z
    .string()
    .regex(/^\+?\d{10,15}$/, { message: "Invalid phone number" }),
});

type StepSchemas = [
  typeof step1Schema | typeof requiredPhoneSchema,
  typeof step2Schema,
  typeof step3Schema,
  typeof step4Schema,
];

type StepData = [
  z.infer<typeof step1Schema>,
  z.infer<typeof step2Schema>,
  z.infer<typeof step3Schema>,
  z.infer<typeof step4Schema>,
];

export const steps = [
  {
    name: "Sign Up",
    number: 1,
  },
  {
    name: "Brand Setup",
    number: 2,
  },
  {
    name: "Clusters",
    number: 3,
  },
  {
    name: "Prompts",
    number: 4,
  },
];

export const MAX_CLUSTERS_NUMBER = 4;
const defaultPrompts: ClusterWithPrompt[] = [];
const MAX_PROMPTS_NUMBER = 16;

const resolveRegisterStepFromRoute = (stepId?: string) => {
  const parsed = Number(stepId);
  if (!Number.isFinite(parsed)) return 0;
  const clamped = Math.max(1, Math.min(4, parsed));
  return clamped - 1;
};

const RegisterForm = () => {
  const { t } = useLocale();
  const { stepId } = useParams<{ stepId?: string }>();
  const [step, setStep] = useState(() => resolveRegisterStepFromRoute(stepId));
  const reset = useDataStore((state) => state.reset);
  const { allClusters } = useDataStore();
  const onboardingStartedTrackedRef = useRef<string>("");

  const { data: authConfig, isLoading } = useQuery({
    queryKey: ["remoteConfig", "authFlow"],
    queryFn: async () => {
      try {
        await fetchAndActivate(remoteConfig);
        const rawValue = getValue(remoteConfig, "authFlow").asString();
        return parseAuthFlowConfig(rawValue);
      } catch {
        return { ...AUTH_FLOW_DEFAULTS };
      }
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const [error, setError] = useState("");
  const [inAppDialogOpen, setInAppDialogOpen] = useState(false);
  const [blockedSourceApp, setBlockedSourceApp] =
    useState<InAppBrowserSource>("unknown");
  const [inAppDetection] = useState(() =>
    detectInAppBrowser(
      typeof navigator !== "undefined" ? navigator.userAgent : "",
    ),
  );
  const inAppBlockTrackedRef = useRef(false);
  const stepSchemas: StepSchemas = [
    !isLoading && Boolean(authConfig?.isPhoneRequired)
      ? requiredPhoneSchema
      : step1Schema,
    step2Schema,
    step3Schema,
    step4Schema,
  ];
  const navigate = useNavigate();
  const {
    loading,
    setLoading,
    user,
    emailVerified,
    loadingStep,
    setLoadingStep,
    refreshUser,
    markOnboardingPromptsSubmitted,
  } = useAuth();
  const [brandId, setBrandId] = useState("");
  const [brand, setBrand] = useState<Brand | null>(null);
  const [company, setCompany] = useState<any | null>(null);
  const [inviteEmail, setInviteEmail] = useState<string | null>(null);
  const meBootstrapRef = useRef<string>("");
  const isPendingEmailVerification = Boolean(
    requireEmailVerification &&
      auth.currentUser &&
      !auth.currentUser.emailVerified,
  );
  const showTermsCopy = step <= 1 && !isPendingEmailVerification;

  const resolveBlockedGoogleFlow = (): "signup" | "invite" | "onboarding" => {
    if (typeof window === "undefined") return "signup";
    const params = new URLSearchParams(window.location.search);
    const inviteToken =
      params.get("invite") || window.localStorage.getItem("invite_token");
    if (inviteToken) return "invite";
    if (params.get("brand")) return "onboarding";
    return "signup";
  };

  const { data: clusters = allClusters, isLoading: isClusterLoading } =
    useQuery({
      queryKey: ["generateClusters", brandId],
      queryFn: async () => {
        const result = await promptsAPI.generateClusters(
          brandId,
          MAX_CLUSTERS_NUMBER,
        );
        return result.clusters.length ? result.clusters : [];
      },
      staleTime: Infinity,
      enabled: !!brandId && !allClusters?.length && step === 2,
    });

  const methods = useForm<StepData[typeof step]>({
    resolver: zodResolver(stepSchemas[step]),
    mode: "onTouched",
  });

  const selectedClusters = methods.watch("clusters") || [];
  const clustersKey = JSON.stringify(
    selectedClusters.map((cluster: { title: string }) => cluster.title),
  );

  const {
    data: clustersWithPrompts = defaultPrompts,
    isLoading: isPromptsLoading,
  } = useQuery({
    queryKey: ["generatePrompts", brandId, clustersKey],
    queryFn: async () => {
      if (!selectedClusters.length) return [];
      const result = await promptsAPI.generateClusterPrompts(
        brandId,
        selectedClusters,
        MAX_PROMPTS_NUMBER,
      );
      return result.clusters.map((cluster) => ({
        title: cluster.title,
        clusterId: cluster.clusterId,
        prompts: cluster.prompts.map((prompt, index) => ({
          text: prompt.text,
          country: String(prompt.country || "")
            .trim()
            .toUpperCase(),
          selected: index === 0 || index === 1,
          isCustom: false,
        })),
      }));
    },
    staleTime: Infinity,
    enabled: !!brandId && selectedClusters.length > 0 && step === 3,
  });

  useEffect(() => {
    setError("");
  }, [step]);

  useEffect(() => {
    const routeStep = resolveRegisterStepFromRoute(stepId);
    setStep((previousStep) =>
      previousStep === routeStep ? previousStep : routeStep,
    );
  }, [stepId]);

  useEffect(() => {
    if (!inAppDetection.isInAppBrowser || inAppBlockTrackedRef.current) return;
    inAppBlockTrackedRef.current = true;
    setBlockedSourceApp(inAppDetection.sourceApp);
    capturePosthogEvent("in_app_browser_google_auth_blocked", {
      source_app: inAppDetection.sourceApp,
      page:
        typeof window !== "undefined" && window.location.pathname
          ? window.location.pathname
          : "unknown",
      flow: resolveBlockedGoogleFlow(),
    });
  }, [inAppDetection.isInAppBrowser, inAppDetection.sourceApp]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get("invite");
    if (!inviteToken) return;
    (async () => {
      try {
        const res = await invitesAPI.resolveInvite(inviteToken);
        const email = res?.invite?.email || "";
        if (email) {
          setInviteEmail(email);
          methods.setValue("email", email, { shouldValidate: true });
          window.localStorage.setItem("invite_token", inviteToken);
        }
      } catch (err: any) {
        setError(err?.message || "Invalid or expired invite");
        window.localStorage.removeItem("invite_token");
      }
    })();
  }, [methods]);

  useEffect(() => {
    const authUid = String(user?.id || auth.currentUser?.uid || "").trim();
    if (!authUid) {
      meBootstrapRef.current = "";
      if (loading) {
        return;
      }
      setStep(0);
      setLoadingStep(false);
      return;
    }
    const bootstrapVerificationState = requireEmailVerification
      ? emailVerified === false
        ? "unverified"
        : "verified_or_unknown"
      : "verification_off";
    const bootstrapKey = `${authUid}:${bootstrapVerificationState}`;
    if (meBootstrapRef.current === bootstrapKey) {
      return;
    }
    meBootstrapRef.current = bootstrapKey;
    let active = true;

    const getMe = async () => {
      try {
        const { user, brands, company } = await authAPI.getMe();
        if (!active) return;
        const verified = requireEmailVerification
          ? emailVerified !== false
          : true;
        const params = new URLSearchParams(window.location.search);
        const verifyParam = params.get("verify");
        const current = `${window.location.pathname}${window.location.search}`;

        if (!verified) {
          setStep(0);
          const verifyState = verifyParam === "invalid" ? "invalid" : "pending";
          const target = `/register/1?verify=${verifyState}`;
          if (current !== target) {
            navigate(target);
          }
          return;
        }

        const rawOnboardStep = Number(user?.onboard_step || 0);
        const effectiveOnboardStep = requireEmailVerification
          ? Math.max(1, rawOnboardStep)
          : rawOnboardStep;
        const LAST_STEP = effectiveOnboardStep >= 3;
        setBrandId(brands.length ? brands[brands.length - 1].brandId : "");
        setCompany(company);
        setBrand(brands[0] || null);
        if (LAST_STEP) {
          navigate(
            user.requires_plan_selection ? `/onboarding/plan` : `/dashboard`,
          );
        } else {
          setStep(effectiveOnboardStep);
          const brand = params.get("brand");
          const verify = params.get("verify");
          const nextStep = effectiveOnboardStep + 1;
          const nextParams = new URLSearchParams();
          if (brand && nextStep === 2) {
            nextParams.set("brand", brand);
          }
          if (
            nextStep === 1 &&
            (verify === "pending" || verify === "invalid")
          ) {
            nextParams.set("verify", verify);
          }
          const nextQuery = nextParams.toString();
          const target = `/register/${nextStep}${nextQuery ? `?${nextQuery}` : ""}`;
          if (current !== target) {
            navigate(target);
          }
        }
      } catch {
        if (!active) return;
        meBootstrapRef.current = "";
        setStep(0);
        const params = new URLSearchParams(window.location.search);
        const verify = params.get("verify");
        const target =
          verify === "pending" || verify === "invalid"
            ? `/register/1?verify=${verify}`
            : `/register/1`;
        const current = `${window.location.pathname}${window.location.search}`;
        if (current !== target) {
          navigate(target);
        }
      } finally {
        if (!active) return;
        setLoadingStep(false);
      }
    };
    void getMe();

    return () => {
      active = false;
    };
  }, [loading, emailVerified, user?.id, navigate, setLoadingStep]);

  useEffect(() => {
    if (step !== 1) return;
    const authMethod = auth.currentUser?.providerData?.some(
      (provider) => String(provider?.providerId || "") === "google.com",
    )
      ? "google"
      : "email";
    const params = new URLSearchParams(window.location.search);
    const prefilledBrandDomain = params.get("brand");
    const signupSource = window.localStorage.getItem("invite_token")
      ? "invite"
      : prefilledBrandDomain
        ? "marketing_home"
        : "direct";
    const trackKey = `${user?.id || "unknown"}:${prefilledBrandDomain || "none"}`;
    if (onboardingStartedTrackedRef.current === trackKey) return;
    onboardingStartedTrackedRef.current = trackKey;
    setOnboardingStartedAt(Date.now());
    capturePosthogEvent("onboarding_started", {
      auth_method: authMethod,
      signup_source: signupSource,
      prefilled_brand_domain: prefilledBrandDomain || undefined,
    });
  }, [step, user?.id]);

  const registerBrand = async (
    brandCountry: string,
    brandWebsite: string,
    brandName: string,
    currentFocus: string,
  ) => {
    const isValid = await methods.trigger();
    if (isValid) {
      setLoadingStep(true);
      try {
        if (!brand) {
          const searchParams = new URLSearchParams(window.location.search);
          const source = searchParams.get("brand") ? "prefilled" : "manual";
          const result = await authAPI.setupBrand({
            country: brandCountry,
            website: brandWebsite,
            name: brandName,
            currentFocus: currentFocus,
            source,
          });
          setBrandId(result.brand.id || "");
          setBrand(result.brand || null);
        }
        setStep(2);
        navigate(`/register/3`);
      } catch (err: unknown) {
        const error = err as AxiosError<{ message: string }>;
        setError(error.response?.data?.message || "Unknown error occurred");
      } finally {
        setLoadingStep(false);
      }
    }
  };

  const resolveSelectionMode = (
    total: number,
    suggested: number,
    custom: number,
  ) => {
    if (total <= 0) return "mixed";
    if (suggested / total >= 0.8) return "kept_suggested";
    if (custom / total >= 0.6) return "mostly_custom";
    return "mixed";
  };

  const registerClusters = async (
    clusters: { title: string; clusterId?: string }[],
  ) => {
    const isValid = await methods.trigger();
    if (isValid) {
      setLoadingStep(true);
      try {
        const totalClustersSelected = Array.isArray(clusters)
          ? clusters.length
          : 0;
        const suggestedClustersSelected = clusters.filter((cluster) =>
          Boolean(cluster?.clusterId),
        ).length;
        const customClustersAdded = Math.max(
          0,
          totalClustersSelected - suggestedClustersSelected,
        );
        capturePosthogEvent("clusters_completed", {
          brand_id: brandId,
          total_clusters_selected: totalClustersSelected,
          suggested_clusters_selected: suggestedClustersSelected,
          custom_clusters_added: customClustersAdded,
          clusters_edited_count: 0,
          selection_mode: resolveSelectionMode(
            totalClustersSelected,
            suggestedClustersSelected,
            customClustersAdded,
          ),
        });
        // const result = await authAPI.setupBrand({
        //     "country": brandCountry,
        //     "website": brandWebsite,
        //     "name": brandName
        // });
        // setBrandId(result.brand.id || "")
        // setBrand(result.brand || null)
        setStep(3);
        navigate(`/register/4`);
      } catch (err: unknown) {
        const error = err as AxiosError<{ message: string }>;
        setError(error.response?.data?.message || "Unknown error occurred");
      } finally {
        setLoadingStep(false);
      }
    }
  };

  const onSubmit = async (data: any) => {
    const isValid = await methods.trigger();
    if (isValid) {
      setLoadingStep(true);
      try {
        const clusters = Array.isArray(data?.clusters) ? data.clusters : [];
        const totalPromptsSelected = clusters.reduce(
          (sum: number, cluster: any) =>
            sum +
            (Array.isArray(cluster?.prompts) ? cluster.prompts.length : 0),
          0,
        );
        const customPromptsAdded = clusters.reduce(
          (sum: number, cluster: any) =>
            sum +
            (Array.isArray(cluster?.prompts)
              ? cluster.prompts.filter((prompt: any) =>
                  Boolean(prompt?.isCustom),
                ).length
              : 0),
          0,
        );
        const suggestedPromptsSelected = Math.max(
          0,
          totalPromptsSelected - customPromptsAdded,
        );
        const clustersWithZeroPrompts = clusters.filter((cluster: any) => {
          const prompts = Array.isArray(cluster?.prompts)
            ? cluster.prompts
            : [];
          return prompts.length === 0;
        }).length;
        const avgPromptsPerCluster = clusters.length
          ? totalPromptsSelected / clusters.length
          : 0;
        capturePosthogEvent("prompts_completed", {
          brand_id: brandId,
          total_prompts_selected: totalPromptsSelected,
          suggested_prompts_selected: suggestedPromptsSelected,
          custom_prompts_added: customPromptsAdded,
          prompts_edited_count: 0,
          clusters_with_0_prompts: clustersWithZeroPrompts,
          avg_prompts_per_cluster: Number(avgPromptsPerCluster.toFixed(2)),
          selection_mode: resolveSelectionMode(
            totalPromptsSelected,
            suggestedPromptsSelected,
            customPromptsAdded,
          ),
        });

        await authAPI.setPrompts(
          clusters.map((cluster: any) => ({
            clusterId: cluster?.clusterId,
            title: cluster?.title,
            prompts: (Array.isArray(cluster?.prompts)
              ? cluster.prompts
              : []
            ).map((prompt: any) => ({
              text: prompt?.text,
              country: prompt?.country,
            })),
          })),
          brandId,
        );
        const onboardingStartedAt = getOnboardingStartedAt();
        const nowIso = new Date().toISOString();
        const timeToCompleteSeconds = onboardingStartedAt
          ? Math.max(0, Math.floor((Date.now() - onboardingStartedAt) / 1000))
          : undefined;
        const primaryCountry =
          String(clusters?.[0]?.country || "")
            .trim()
            .toUpperCase() || undefined;
        capturePosthogEvent("onboarding_completed", {
          brand_id: brandId,
          time_to_complete_seconds: timeToCompleteSeconds,
          clusters_selected: clusters.length,
          prompts_selected: totalPromptsSelected,
          primary_country: primaryCountry,
        });
        setPosthogPersonProperties({
          onboarding_completed: true,
          onboarding_completed_at: nowIso,
        });
        setBrandOnboardingCompletedAt(brandId, nowIso);
        clearOnboardingStartedAt();
        window.sessionStorage.setItem("nobori_redirect_to_strategy_once", "1");
        markOnboardingPromptsSubmitted();
        navigate(`/onboarding/plan`, {
          replace: true,
          state: { fromOnboarding: true },
        });
        reset();
        void refreshUser().catch((refreshError) => {
          console.warn(
            "Failed to refresh user after prompts setup",
            refreshError,
          );
        });
      } catch (err: unknown) {
        const error = err as AxiosError<{ message: string }>;
        setError(error.response?.data?.message || "Unknown error occurred");
      } finally {
        setLoadingStep(false);
      }
    }
  };

  const handleLogout = async () => {
    setLoading(true);
    try {
      const [apiLogoutResult, firebaseSignOutResult] = await Promise.allSettled(
        [authAPI.logout(), signOut(auth)],
      );

      if (
        apiLogoutResult.status === "rejected" &&
        firebaseSignOutResult.status === "rejected"
      ) {
        toast.error(t("legacy.couldnt.log.out.1diajr7"), {
          icon: (
            <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
              <TriangleAlert className="w-4 h-4 text-black" />
            </div>
          ),
          position: "bottom-right",
        });
      }

      navigate("/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const handleStepNavigate = (st: Record<string, string | number>) => {
    if (Number(st.number) != 1 && Number(st.number) <= step) {
      setStep(Number(st.number) - 1);
      navigate(`/register/${Number(st.number)}`);
    }
  };

  const handleGoogleSignIn = async () => {
    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : "";
    const flow = resolveBlockedGoogleFlow();
    const inAppDetection = detectInAppBrowser(userAgent);
    if (inAppDetection.isInAppBrowser) {
      setBlockedSourceApp(inAppDetection.sourceApp);
      setInAppDialogOpen(true);
      setError("");
      return;
    }

    const mode = resolveGoogleSignInMode(userAgent);
    if (mode === "blocked") {
      setBlockedSourceApp("unknown");
      setInAppDialogOpen(true);
      setError("");
      capturePosthogEvent("in_app_browser_google_auth_blocked", {
        source_app: "unknown",
        page:
          typeof window !== "undefined" && window.location.pathname
            ? window.location.pathname
            : "unknown",
        flow,
      });
      return;
    }

    setLoadingStep(true);
    try {
      if (mode === "redirect") {
        await signInWithRedirect(auth, googleProvider);
        return;
      }
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      setError(getGoogleAuthErrorMessage(err));
    } finally {
      setLoadingStep(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <div className="d-flex flex-column-fluid flex-row-auto justify-content-center mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 w-full">
          <div className={` flex flex-col items-center mx-6  mb-10 bg-white`}>
            <div
              className={`flex w-full mx-6 justify-center flex-col ${step > 1 ? "sm:max-w-[630px]" : "sm:max-w-[466px]"}`}
            >
              <div className="sticky top-0 pt-10 z-50 backdrop-blur-md bg-white/30 [mask-image:linear-gradient(to_bottom,black_85%,transparent_100%)]">
                <div className="flex justify-center mb-6">
                  <img src={noboriLogo}></img>
                </div>

                <div className="flex justify-between text-sm mx-0 my-10">
                  {steps.map((st) => {
                    return (
                      <React.Fragment key={st.number}>
                        <div
                          className={`flex flex-col items-center text-[#393B40] px-3 md:px-0 ${step + 1 >= st.number && "text-[#FF5E00]"}  `}
                        >
                          <div
                            onClick={() => handleStepNavigate(st)}
                            className={`flex justify-center items-center w-[32px] h-[32px] border rounded-full border-[1.5px]  ${step + 1 >= st.number && "border-[#FF5E00] font-medium"} ${step + 1 > st.number && "bg-[#FF5E00]"}`}
                          >
                            {step + 1 > st.number ? (
                              <Check className="text-white" />
                            ) : (
                              `0${st.number}`
                            )}
                          </div>
                          <div
                            className={`text-[#737373] font-medium text-sm hidden md:block ${step + 1 >= st.number && "text-[#FF5E00]"} `}
                          >
                            {st.name}
                          </div>
                        </div>
                        {st.number <= 3 && (
                          <div className="grow relative translate-y-1/3">
                            <Separator
                              className={`${st.number < step + 1 && "bg-[#FF5E00]"} `}
                            />
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
              <div
                className={`w-full mx-auto ${step > 1 ? "sm:max-w-[630px]" : "sm:max-w-[466px]"} p-5 border bg-white rounded-lg`}
              >
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                  {step === 0 && (
                    <Step1
                      loadingStep={loadingStep}
                      methods={methods}
                      setError={setError}
                      setLoadingStep={setLoadingStep}
                      error={error}
                      handleGoogleSignIn={handleGoogleSignIn}
                      isGoogleSignInBlockedInAppBrowser={
                        inAppDetection.isInAppBrowser
                      }
                      onOpenGoogleSignInBrowserHelp={() => {
                        setBlockedSourceApp(inAppDetection.sourceApp);
                        setInAppDialogOpen(true);
                      }}
                      prefilledEmail={inviteEmail || undefined}
                    />
                  )}
                  {step === 1 && (
                    <Step2
                      loadingStep={loadingStep}
                      registerBrand={registerBrand}
                      error={error}
                      company={company}
                      prefilled={true}
                      brand={brand}
                    />
                  )}
                  {step === 2 && (
                    <Step3
                      brandId={brandId}
                      loadingStep={loadingStep}
                      error={error}
                      registerClusters={registerClusters}
                      company={company || brand}
                      clusters={clusters}
                      isLoading={isClusterLoading}
                    />
                  )}
                  {step === 3 && (
                    <Step4
                      brandId={brandId}
                      clustersWithPrompts={clustersWithPrompts}
                      isLoading={isPromptsLoading}
                      loadingStep={loadingStep}
                      error={error}
                      company={company || brand}
                      submitLabel="Continue"
                    />
                  )}
                </form>
              </div>
              {showTermsCopy ? (
                <div className="mt-6 mx-auto text-center text-xs text-muted-foreground max-w-[229px]">
                  <LocalizedRichText
                    id="auth.termsNotice"
                    values={{
                      terms: (
                        <Link
                          to="https://nobori.ai/terms-of-service"
                          target="_blank"
                          className="ml-1 underline underline-offset-1"
                        >
                          <LocalizedText id="legacy.terms.of.service.1nx66rw" />
                        </Link>
                      ),
                      privacy: (
                        <Link
                          target="_blank"
                          className="ml-1 underline underline-offset-1"
                          to="https://nobori.ai/privacy-policy"
                        >
                          <LocalizedText id="legacy.privacy.policy.ot76bn" />
                        </Link>
                      ),
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
          <div
            style={{ backgroundImage: `url(${registrationBg})` }}
            className="p-9 bg-cover bg-center bg-no-repeat relative sticky flex justify-center top-0 hidden overflow-hidden lg:flex flex-grow bg-black h-screen"
          >
            {isClusterLoading ? (
              <ClusterPromptsProcessing
                isPrompts={false}
                brand={brand}
              ></ClusterPromptsProcessing>
            ) : isPromptsLoading ? (
              <ClusterPromptsProcessing
                isPrompts={true}
                brand={brand}
              ></ClusterPromptsProcessing>
            ) : (
              <div className="w-full max-w-lg flex-col h-full flex items-start justify-start mt-[100px]">
                {stepDescription(brand)[step]?.logo}
                <AlertDialog>
                  <AlertDialogTrigger
                    disabled={step < 1 || isClusterLoading || isPromptsLoading}
                    className="w-full flex justify-center"
                  >
                    <div
                      className={`absolute top-4 right-4 flex items-center justify-center ${step < 1 || isClusterLoading || isPromptsLoading ? "text-[#d0a197] cursor-not-allowed" : "text-[#FFFFFF] cursor-pointer"}  px-4 py-2 rounded-full gap-2 text-sm bg-white/20`}
                    >
                      <LogOut size={16} />
                      <LocalizedText id="legacy.log.out.mgztlb" />
                    </div>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-center">
                        <LocalizedText id="legacy.are.you.sure.you.want.to.log.out.1y7m1mo" />
                      </AlertDialogTitle>
                      <AlertDialogDescription className="text-center">
                        <LocalizedText id="legacy.your.current.progress.will.be.saved.17clrki" />
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="cursor-pointer">
                        <LocalizedText id="legacy.cancel.18x3nqb" />
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleLogout}
                        className="hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
                      >
                        <LocalizedText id="legacy.log.out.mgz4hb" />
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                {stepDescription(brand)[step]?.title}
                <div className="text-[#737373] w-full flex flex-col">
                  {stepDescription(brand)[step]?.description}

                  {stepDescription(brand)[step]?.feature1}
                  {stepDescription(brand)[step]?.feature2}
                  {stepDescription(brand)[step]?.feature3}
                  {stepDescription(brand)[step]?.additionalText && (
                    <p className="text-xs">
                      {stepDescription(brand)[step]?.additionalText}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <InAppBrowserGoogleAuthDialog
          open={inAppDialogOpen}
          onOpenChange={setInAppDialogOpen}
          sourceApp={blockedSourceApp}
        />
      </div>
    </FormProvider>
  );
};

export default RegisterForm;
